import parseJson from "../parse-json";
import {BackendFetch, LegacyOrder, Processes, TypedCommand} from "@hantera/portal-app";
import cache from '@/backend/cache';
import {reactive} from "vue";

const saving: {
  [orderId: string]: boolean
} = {};

const capturing: {
  [paymentId: string]: boolean
} = {};

const isLoading = reactive<Map<string, number>>(new Map<string, number>());

export enum PaymentRetryType {
  capture = 'capture',
  refund = 'refund',
  cancellation = 'cancellation',
}
export enum PaymentState {
  paid = 'paid',
  refunded = 'refunded',
  cancelled = 'cancelled',
}

function pushLoading(orderId: string) {
  isLoading.set(orderId, (isLoading.get(orderId) ?? 0) + 1);
}

function popLoading(orderId: string) {
  const current = (isLoading.get(orderId) ?? 0); 
  if (current > 0) {
    isLoading.set(orderId, current - 1);
  }
}

const sendQueue: {
  orderId: string
  message: string
  fields: any
  commands: { type: string, body: any }[]
}[] = []

let isSending = false



export default function (portalFetch: BackendFetch, processes: Processes) {

  async function send() {
    if (isSending) {
      return
    }

    const next = sendQueue.shift()
    
    if (!next) {
      return
    }
    
    isSending = true

    pushLoading(next.orderId);
    try {
      const res = await portalFetch('/legacy-orders/addCommandGroup', {
        method: 'POST',
        body: JSON.stringify(next),
      });

      if (res.status === 200) {
        cache.legacyOrders.addOrUpdate(parseJson(await res.text()));
      }
    }
    finally {
      popLoading(next.orderId);
      isSending = false
    }
    
    if (sendQueue.length > 0) {
      // Recursive call, should be safe as there should not be more than a handful in queue at most
      await send()
    }
  }

  
  async function legacyRefresh(orderId: string): Promise<LegacyOrder | undefined> {

    pushLoading(orderId);
    
    try {

      const res = await portalFetch('/legacy-orders/legacyRefresh', {
        method: 'POST',
        body: JSON.stringify({
          orderId
        }),
      });

      if (res.ok) {
        cache.legacyOrders.addOrUpdate(parseJson(await res.text()));
        return cache.legacyOrders.get(orderId);
      }

      throw 'Failed to refresh legacy read model.';
    }
    finally
    {
      popLoading(orderId);
    }
  }
  
  return {

    isLoading,
    
    /**
     * Get a specific order. Will return a reactive cached version if available, that will be rehydrated against backend
     * @param orderId 
     */
    async get(orderId: string): Promise<LegacyOrder | undefined> {
      const existing = cache.legacyOrders.get(orderId);
      if (existing) {
        refresh(orderId);
        return existing;
      }

      await refresh(orderId);

      return cache.legacyOrders.get(orderId);
    },

    /**
     * Makes a special call to legacy backend to rebuild it's read model.
     * @param orderId 
     * @returns 
     */
    legacyRefresh,

    buildCommandGroup(orderId: string, message: string, fields: { [name: string]: any }) {
      const group = {
        orderId,
        message,
        fields,
        commands: <{ type: string, body: any }[]>[],
      }

      const api = {
        command<T>(type: TypedCommand<T>, body: T) {
          group.commands.push({
            type: type.type,
            body: body,
          });
          return api;
        },
        async send() {
          
          sendQueue.push(group)
          
          await send()
        }
      };
      return api;
    },

    /**
     * Saves all pending command
     * @param orderId 
     */
    async save<T>(orderId: string) {
      if (saving[orderId]) {
        console.log('Blocked saving of order. Already in progress.');
        return;
      }

      saving[orderId] = true;

      const proc = processes.createProcess({ description: 'Saving order'});
      pushLoading(orderId);
      try {
        const res = await portalFetch('/legacy-orders/save', {
          method: 'POST',
          body: JSON.stringify({
            orderId,
          }),
        });

        if (res.status === 200) {
          cache.legacyOrders.addOrUpdate(parseJson(await res.text()));
          proc.complete();
        }
        
        if (res.status !== 200) {
          proc.fail({ message: 'Saving order failed. Server responded: ' + res.status });
        }
      } finally {
        popLoading(orderId);
        delete saving[orderId];
      }
    },

    /**
     * Removes all draft command groups starting from the specified one. All subsequent groups will be removed to maintain integrity of those commands.
     * @param orderId 
     */
    async removeCommandGroup<T>(orderId: string, commandGroupId: string) {

      pushLoading(orderId);
      try {
        const res = await portalFetch('/legacy-orders/removeCommandGroup', {
          method: 'POST',
          body: JSON.stringify({
            orderId,
            commandGroupId,
          }),
        });

        if (res.status === 200) {
          cache.legacyOrders.addOrUpdate(parseJson(await res.text()));
        }
      }
      finally {
        popLoading(orderId);
      }
    },

    /**
     * Retries refunding a failed payment refund.
     * @param orderId 
     * @param paymentId 
     * @returns 
     */
    async retryPayment(orderId: string, paymentId: string, type: PaymentRetryType) {
      if (capturing[paymentId]) {
        console.log(`Blocked payment ${type}. Already in progress.`);
        return;
      }
      
      capturing[paymentId] = true;
      const proc = processes.createProcess({ description: 'Retrying payment ' + type });
      
      pushLoading(orderId);
      try {

        const res = await portalFetch('/legacy-payments/retry', {
          method: 'POST',
          body: JSON.stringify({
            orderId,
            paymentId,
            type,
          }),
        });

        if (res.ok) {
          var resBody = parseJson(await res.text());
          if (resBody.error !== 'NONE') {
            proc.fail({ message: `(${resBody.error}) ${resBody.message}` });
            return;
          }
          
          await refresh(orderId)

          proc.complete();
        }

        if (res.status !== 200) {
          proc.fail({ message: 'Server responded with: '+ res.status });
        }
      } finally {
        popLoading(orderId);
        delete capturing[paymentId];
      }
    },

    /**
     * Retries refunding a failed payment refund.
     * @param orderId 
     * @param paymentId 
     * @returns 
     */
    async setPaymentState(orderId: string, paymentId: string, state: PaymentState) {
      if (capturing[paymentId]) {
        console.log(`Blocked payment ${state}. Already in progress.`);
        return;
      }

      capturing[paymentId] = true;
      const proc = processes.createProcess({ description: 'Setting payment state to ' + state })
      
      pushLoading(orderId);
      try {

        const res = await portalFetch('/legacy-payments/setState', {
          method: 'POST',
          body: JSON.stringify({
            orderId,
            paymentId,
            state,
          }),
        });

        if (res.ok) {
          var resBody = parseJson(await res.text());
          if (resBody.error && resBody.error !== 'NONE') {
            proc.fail(resBody.message);
            return;
          }
          
          await refresh(orderId)

          proc.complete();
        }

        if (res.status !== 200) {
          proc.fail({ message: 'Server responded with: '+ res.status });
        }
      } finally {
        popLoading(orderId);
        delete capturing[paymentId];
      }
    },

    refresh,
  };

  async function refresh(orderId: string) {
    pushLoading(orderId);
    try {
      const res = await portalFetch('/legacy-orders/getOrder', {
        method: 'POST',
        body: JSON.stringify({
          orderId
        }),
      });

      if (res.ok) {
        cache.legacyOrders.addOrUpdate(parseJson(await res.text()));
      } else if (res.status === 404) {
        legacyRefresh(orderId)
      }
    }
    finally {
      popLoading(orderId);
    }
  }

}